import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, ArrowRight, Shield, Wrench, HeartPulse, Clipboard } from 'lucide-react';
import { servicesData } from '../mockData';

export const MaintenancePage = () => {
  const { maintenance } = servicesData;

  const maintenanceTypes = [
    {
      icon: <Shield size={32} />,
      title: 'Manutenção Preventiva',
      description: 'Manutenção programada regular para prevenir falhas e estender a vida útil dos equipamentos'
    },
    {
      icon: <Wrench size={32} />,
      title: 'Manutenção Corretiva',
      description: 'Reparos especializados quando necessário, utilizando peças genuínas'
    },
    {
      icon: <HeartPulse size={32} />,
      title: 'Otimização de Desempenho',
      description: 'Calibração e otimização regular para manter o desempenho máximo'
    },
    {
      icon: <Clipboard size={32} />,
      title: 'Contratos Personalizados',
      description: 'Elaboramos contratos sob medida para as necessidades da sua instalação'
    }
  ];

  return (
    <div className="min-h-screen pt-24 pb-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-cyan-50 py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-6">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900">
              {maintenance.title}
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {maintenance.description}
            </p>
          </div>
        </div>
      </section>

      {/* Service Types */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Soluções Abrangentes de Manutenção
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {maintenanceTypes.map((type, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6 text-center space-y-4">
                  <div className="mx-auto h-16 w-16 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
                    {type.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{type.title}</h3>
                  <p className="text-gray-600 text-sm">{type.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Por Que Escolher Nosso Serviço de Manutenção?
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {maintenance.benefits.map((benefit, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6 flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <CheckCircle className="text-green-600" size={24} />
                  </div>
                  <p className="text-gray-700 font-medium">{benefit}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Equipment Coverage */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Equipamentos Cobertos
            </h2>
            <p className="text-xl text-gray-600">
              Prestamos manutenção para toda nossa linha de equipamentos
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {['Ressonância Magnética', 'Tomógrafo', 'Ultrassom', 'Densitometria Óssea', 'Mamógrafo', 'Raio-X', 'CR', 'DR', 'Workstation', 'PACS'].map((item, index) => (
              <Card key={index} className="hover:border-blue-600 transition-colors duration-300">
                <CardContent className="p-4 text-center space-y-2">
                  <div className="mx-auto h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="text-blue-600" size={20} />
                  </div>
                  <p className="font-semibold text-gray-900 text-sm">{item}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contratos Section */}
      <section className="py-16 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <Card className="border-2 border-blue-600">
              <CardContent className="p-12 text-center space-y-6">
                <div className="mx-auto h-20 w-20 bg-blue-100 rounded-full flex items-center justify-center">
                  <Clipboard className="text-blue-600" size={40} />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">
                  Contratos de Manutenção Customizados
                </h2>
                <p className="text-xl text-gray-600">
                  Oferecemos contratos de manutenção preventiva e corretiva personalizados para atender às necessidades específicas da sua instalação. Entre em contato para uma proposta sob medida.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
                  <Card className="bg-blue-50">
                    <CardContent className="p-6">
                      <h3 className="font-bold text-lg text-gray-900 mb-2">Manutenção Preventiva</h3>
                      <p className="text-gray-600 text-sm">Inspeções regulares programadas para evitar falhas e maximizar a vida útil dos equipamentos</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-blue-50">
                    <CardContent className="p-6">
                      <h3 className="font-bold text-lg text-gray-900 mb-2">Manutenção Corretiva</h3>
                      <p className="text-gray-600 text-sm">Reparos especializados quando necessário, com peças originais e técnicos certificados</p>
                    </CardContent>
                  </Card>
                </div>
                <Link to="/contact">
                  <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white mt-6">
                    Solicitar Proposta Personalizada
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <div className="space-y-6">
            <h2 className="text-3xl sm:text-4xl font-bold text-white">
              Mantenha Seus Equipamentos Funcionando com Desempenho Máximo
            </h2>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              Entre em contato para discutir suas necessidades de manutenção
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg">
                Entre em Contato
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};